import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Warning, XCircle, Info, Sparkle, CircleNotch, X } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useAIStore } from "../../stores/ai";
import type { CompileError } from "../../types/compilation";

interface ErrorListProps {
  errors: CompileError[];
  onNavigate?: (line: number) => void;
}

function getSeverityConfig(severity: string) {
  switch (severity) {
    case "error":
      return {
        icon: XCircle,
        color: "text-[var(--color-soft-red)]",
        bg: "bg-[var(--color-soft-red)]/8",
      };
    case "warning":
      return {
        icon: Warning,
        color: "text-[var(--color-ember-orange)]",
        bg: "bg-[var(--color-ember-orange)]/8",
      };
    default:
      return {
        icon: Info,
        color: "text-[var(--color-cool-azure)]",
        bg: "bg-[var(--color-cool-azure)]/8",
      };
  }
}

function ExplainPopover({ explanation, onClose }: { explanation: string; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -4, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -4, scale: 0.97 }}
      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "mt-2 rounded-[var(--radius-lg)] p-3",
        "bg-[var(--bg-base)] border border-[var(--border-subtle)]",
        "shadow-[var(--glow-ai)]",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-[var(--color-lever-orange)] mb-1.5">
          <Sparkle size={9} weight="fill" />
          AI Explanation
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-100"
        >
          <X size={10} />
        </button>
      </div>
      <p className="text-xs leading-relaxed text-[var(--text-secondary)]">
        {explanation}
      </p>
    </motion.div>
  );
}

function ErrorRow({ error, idx, onNavigate }: { error: CompileError; idx: number; onNavigate?: (line: number) => void }) {
  const explainError = useAIStore((s) => s.explainError);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [explaining, setExplaining] = useState(false);

  const config = getSeverityConfig(error.severity);
  const Icon = config.icon;

  const handleExplain = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (explaining) return;
      if (explanation) {
        setExplanation(null);
        return;
      }
      setExplaining(true);
      try {
        const resp = await explainError(error.message, `Line ${error.line ?? "?"}`);
        setExplanation(resp.result);
      } catch {
        setExplanation("Could not generate explanation. Check your AI provider configuration.");
      } finally {
        setExplaining(false);
      }
    },
    [explaining, explanation, explainError, error.message, error.line],
  );

  return (
    <div
      key={`${error.severity}-${error.line}-${idx}`}
      onClick={() => error.line && onNavigate?.(error.line)}
      className={cn(
        "border-b border-[var(--border-hairline)] px-3 py-2",
        "hover:bg-[var(--bg-surface-hover)]",
        "transition-colors",
        error.line && onNavigate ? "cursor-pointer" : "",
      )}
    >
      <div className="flex items-start gap-2">
        <div className={cn("mt-0.5 shrink-0 rounded-[var(--radius-sm)] p-0.5", config.bg)}>
          <Icon size={12} weight="fill" className={config.color} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-[var(--text-primary)]">{error.message}</p>
          {error.line && (
            <p className="mt-0.5 text-xs text-[var(--text-muted)]">
              Line {error.line}
              {error.file ? ` in ${error.file}` : ""}
            </p>
          )}
        </div>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleExplain}
          title="Explain with AI"
          className={cn(
            "shrink-0 mt-0.5 rounded-[var(--radius-sm)] p-1",
            "text-[var(--text-muted)] hover:text-[var(--color-lever-orange)]",
            "hover:bg-[var(--color-lever-orange)]/10",
            "transition-colors duration-100",
            explanation && "text-[var(--color-lever-orange)] bg-[var(--color-lever-orange)]/10",
          )}
        >
          {explaining ? (
            <CircleNotch size={12} weight="bold" className="animate-spin" />
          ) : (
            <Sparkle size={12} weight={explanation ? "fill" : "bold"} />
          )}
        </motion.button>
      </div>

      <AnimatePresence>
        {explanation && (
          <ExplainPopover
            explanation={explanation}
            onClose={() => setExplanation(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export function ErrorList({ errors, onNavigate }: ErrorListProps) {
  if (errors.length === 0) {
    return (
      <div className="flex items-center justify-center p-6 text-xs text-[var(--text-muted)]">
        No errors
      </div>
    );
  }

  return (
    <div className="overflow-y-auto">
      {errors.map((error, idx) => (
        <ErrorRow key={`${error.severity}-${error.line}-${idx}`} error={error} idx={idx} onNavigate={onNavigate} />
      ))}
    </div>
  );
}
